import type { ComponentType } from 'react';
import { useEffect, useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Bell, Eye, EyeOff, Lock, User } from 'lucide-react';

import { useSetCustomerPassword, useUpdateCustomerPassword } from '@/hooks/customer/useCustomerPassword';
import { usePasswordLoginAvailability } from '@/hooks/auth/usePasswordLoginAvailability';
import {
  CustomerPasswordChangeRequestError,
  type CustomerPasswordChangeErrorItem,
} from '@/services/customer/customer-password.service';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { useCustomerAuth } from '@/contexts/CustomerAuthContext';
import { formatPasswordPolicyRules } from '@/lib/auth/password-policy';
import { resolvePasswordValidationMessage } from '@/lib/auth/password-validation';

interface SettingsCategory {
  id: string;
  titleKey: string;
  descriptionKey: string;
  icon: ComponentType<{ className?: string }>;
  badgeKey?: string;
}

const settingsCategories: SettingsCategory[] = [
  {
    id: 'security',
    titleKey: 'customer.settings.categories.security.title',
    descriptionKey: 'customer.settings.categories.security.description',
    icon: Lock,
  },
  {
    id: 'profile',
    titleKey: 'customer.settings.categories.profile.title',
    descriptionKey: 'customer.settings.categories.profile.description',
    icon: User,
    badgeKey: 'customer.settings.categories.badges.comingSoon',
  },
  {
    id: 'notifications',
    titleKey: 'customer.settings.categories.notifications.title',
    descriptionKey: 'customer.settings.categories.notifications.description',
    icon: Bell,
    badgeKey: 'customer.settings.categories.badges.comingSoon',
  },
];

const createSchema = (t: TFunction, hasPassword: boolean) =>
  z
    .object({
      currentPassword: z.string(),
      password: z.string().min(1, t('customer.settings.password.validation.required', 'Please fill out this field.')),
      confirmPassword: z
        .string()
        .min(1, t('customer.settings.password.validation.required', 'Please fill out this field.')),
    })
    .superRefine((values, ctx) => {
      if (hasPassword && !values.currentPassword.trim()) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('customer.settings.password.validation.required', 'Please fill out this field.'),
          path: ['currentPassword'],
        });
      }

      if (values.password !== values.confirmPassword) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: t('customer.settings.password.validation.mismatch', 'Passwords do not match.'),
          path: ['confirmPassword'],
        });
      }
    });

type FormValues = z.infer<ReturnType<typeof createSchema>>;

const pointerFieldMap: Record<string, keyof FormValues> = {
  '/data/attributes/current_password': 'currentPassword',
  '/data/attributes/password': 'password',
  '/data/attributes/password_confirmation': 'confirmPassword',
};

const passwordValidationMessages: Record<
  string,
  { key: string; fallback: string }
> = {
  'The current password is incorrect.': {
    key: 'customer.settings.password.validation.currentIncorrect',
    fallback: 'The current password is incorrect.',
  },
  'The password is incorrect.': {
    key: 'customer.settings.password.validation.currentIncorrect',
    fallback: 'The current password is incorrect.',
  },
  'Use the change password endpoint to update your password.': {
    key: 'customer.settings.password.validation.useChangeEndpoint',
    fallback: 'Use the change password endpoint to update your password.',
  },
  'validation.password.mixed': {
    key: 'customer.settings.password.validation.mixed',
    fallback: 'Password must include upper and lower case letters.',
  },
  'validation.password.symbols': {
    key: 'customer.settings.password.validation.symbols',
    fallback: 'Password must include at least one symbol.',
  },
  'validation.password.numbers': {
    key: 'customer.settings.password.validation.numbers',
    fallback: 'Password must include at least one number.',
  },
};

function resolveMinLengthMessage(t: TFunction, message: string) {
  const match = message.match(/at least\s+(\d+)\s+characters?/i);
  if (!match) {
    return undefined;
  }

  const count = Number(match[1]);
  if (!Number.isFinite(count)) {
    return undefined;
  }

  return t('customer.settings.password.validation.minLength', 'Password must be at least {{count}} characters.', {
    count,
  });
}

function resolvePasswordErrorMessage(t: TFunction, error: CustomerPasswordChangeErrorItem) {
  const candidates = [error.detail, error.title, error.code];
  let fallback: string | undefined;

  for (const message of candidates) {
    if (!message) {
      continue;
    }
    if (!fallback) {
      fallback = message;
    }

    const minLengthMessage = resolveMinLengthMessage(t, message);
    if (minLengthMessage) {
      return minLengthMessage;
    }

    const mapped = passwordValidationMessages[message];
    if (mapped) {
      return t(mapped.key, mapped.fallback);
    }

    const resolved = resolvePasswordValidationMessage(t, message);
    if (resolved) {
      return resolved;
    }
  }

  return fallback;
}

function mapJsonApiErrors(t: TFunction, errors?: CustomerPasswordChangeErrorItem[]) {
  const fieldErrors: Partial<Record<keyof FormValues, string[]>> = {};
  const generalMessages: string[] = [];

  (errors ?? []).forEach((error) => {
    const pointer = error.source?.pointer;
    const message = resolvePasswordErrorMessage(t, error);
    if (pointer && message && pointerFieldMap[pointer]) {
      const field = pointerFieldMap[pointer];
      const next = fieldErrors[field] ?? [];
      next.push(message);
      fieldErrors[field] = next;
      return;
    }

    if (message) {
      generalMessages.push(message);
    }
  });

  return {
    fieldErrors,
    generalMessage: generalMessages.filter(Boolean).join(' '),
  };
}

export default function CustomerSettingsPage() {
  const { t } = useTranslation();
  const { user, checkAuth } = useCustomerAuth();
  const hasPassword = user?.hasPassword ?? true;
  const isSetFlow = !hasPassword;
  const schema = useMemo(() => createSchema(t, hasPassword), [t, hasPassword]);
  const updatePassword = useUpdateCustomerPassword();
  const setPassword = useSetCustomerPassword();
  const { policy } = usePasswordLoginAvailability('customer');
  const passwordRules = useMemo(() => formatPasswordPolicyRules(t, policy), [policy, t]);
  const [apiError, setApiError] = useState<string | null>(null);
  const [activeCategory, setActiveCategory] = useState<string>('security');
  const isSubmitting = updatePassword.isPending || setPassword.isPending;
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const translatedCategories = useMemo(
    () =>
      settingsCategories.map((category) => ({
        ...category,
        title: t(category.titleKey),
        description: t(category.descriptionKey),
        badge: category.badgeKey ? t(category.badgeKey) : undefined,
      })),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      currentPassword: '',
      password: '',
      confirmPassword: '',
    },
  });

  useEffect(() => {
    form.reset({ currentPassword: '', password: '', confirmPassword: '' });
    setApiError(null);
  }, [form, hasPassword]);

  const onSubmit = async (values: FormValues) => {
    setApiError(null);
    form.clearErrors();

    try {
      const response = isSetFlow
        ? await setPassword.mutateAsync({
            password: values.password,
            passwordConfirmation: values.confirmPassword,
          })
        : await updatePassword.mutateAsync({
            currentPassword: values.currentPassword,
            password: values.password,
            passwordConfirmation: values.confirmPassword,
          });

      toast.success(
        response.message ??
          t(
            isSetFlow ? 'customer.settings.password.setSuccess' : 'customer.settings.password.success',
            isSetFlow ? 'Password set successfully.' : 'Password updated successfully.'
          )
      );
      form.reset();
      if (isSetFlow) {
        void checkAuth();
      }
    } catch (error: unknown) {
      if (error instanceof CustomerPasswordChangeRequestError) {
        if (error.status === 401) {
          setApiError(t('customer.settings.password.unauthorized', 'Please sign in again to update your password.'));
          return;
        }
        if (error.status === 403) {
          setApiError(
            t('customer.settings.password.forbidden', 'Your session does not have permission to update this password.')
          );
          return;
        }

        const { fieldErrors, generalMessage } = mapJsonApiErrors(t, error.errors);
        Object.entries(fieldErrors).forEach(([field, messages]) => {
          const uniqueMessages = Array.from(new Set(messages));
          form.setError(field as keyof FormValues, { message: uniqueMessages.join(' • ') });
        });
        if (generalMessage) {
          setApiError(generalMessage);
          return;
        }
        if (Object.keys(fieldErrors).length > 0) {
          return;
        }
      }

      setApiError(
        t(
          isSetFlow ? 'customer.settings.password.setError' : 'customer.settings.password.error',
          isSetFlow ? 'Unable to set password. Please try again.' : 'Unable to update password. Please try again.'
        )
      );
    }
  };

  return (
    <div className="space-y-8">
      <div className="space-y-2">
        <h1 className="text-3xl font-semibold tracking-tight">{t('customer.settings.title', 'Account settings')}</h1>
        <p className="text-muted-foreground">
          {t('customer.settings.subtitle', 'Manage security details for your customer portal account.')}
        </p>
      </div>

      <Card className="w-full">
        <CardHeader>
          <CardTitle className="text-base">{t('customer.settings.categoriesTitle', 'Settings categories')}</CardTitle>
          <CardDescription>
            {t(
              'customer.settings.categoriesDescription',
              'Configure your account and platform preferences.'
            )}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-3">
          {translatedCategories.map((category) => {
            const Icon = category.icon;
            const isActive = activeCategory === category.id;
            const isDisabled = Boolean(category.badgeKey);

            return (
              <button
                key={category.id}
                onClick={() => {
                  if (!isDisabled) {
                    setActiveCategory(category.id);
                  }
                }}
                className={`flex items-center gap-2 rounded-xl border px-4 py-3 text-left transition-all ${
                  isActive
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'border-transparent bg-muted hover:bg-muted/80'
                } ${isDisabled ? 'cursor-not-allowed opacity-70' : ''}`}
                disabled={isDisabled}
              >
                <Icon className="h-4 w-4" />
                <div className="flex flex-col">
                  <span className="text-sm font-medium leading-tight">{category.title}</span>
                  <span className="text-xs text-muted-foreground">{category.description}</span>
                </div>
                {category.badge ? (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {category.badge}
                  </Badge>
                ) : null}
              </button>
            );
          })}
        </CardContent>
      </Card>

      {activeCategory === 'security' ? (
        <Card className="max-w-2xl">
          <CardHeader>
            <CardTitle className="text-xl">
              {t(
                isSetFlow ? 'customer.settings.password.setTitle' : 'customer.settings.password.title',
                isSetFlow ? 'Set password' : 'Password'
              )}
            </CardTitle>
            <CardDescription>
              {t(
                isSetFlow ? 'customer.settings.password.setDescription' : 'customer.settings.password.description',
                isSetFlow
                  ? 'Set a password to use email and password sign-in for this account.'
                  : 'Update the password you use to sign in with email and password.'
              )}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {apiError ? (
              <Alert variant="destructive">
                <AlertDescription>{apiError}</AlertDescription>
              </Alert>
            ) : null}
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
              {!isSetFlow ? (
                <FormField
                  control={form.control}
                  name="currentPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('customer.settings.password.currentLabel', 'Current password')}</FormLabel>
                      <FormControl>
                        <div className="relative">
                          <Input
                            type={showCurrentPassword ? 'text' : 'password'}
                            autoComplete="current-password"
                            placeholder={t(
                              'customer.settings.password.currentPlaceholder',
                              'Enter your current password'
                            )}
                            disabled={isSubmitting}
                            className="pr-10"
                            {...field}
                          />
                          <button
                            type="button"
                            onClick={() => setShowCurrentPassword((previous) => !previous)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
                            aria-label={t(
                              showCurrentPassword
                                ? 'customer.settings.password.hidePassword'
                                : 'customer.settings.password.showPassword',
                              showCurrentPassword ? 'Hide password' : 'Show password'
                            )}
                            title={t(
                              showCurrentPassword
                                ? 'customer.settings.password.hidePassword'
                                : 'customer.settings.password.showPassword',
                              showCurrentPassword ? 'Hide password' : 'Show password'
                            )}
                            disabled={isSubmitting}
                          >
                            {showCurrentPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              ) : null}
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customer.settings.password.newLabel', 'New password')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showNewPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder={t('customer.settings.password.newPlaceholder', 'Enter a new password')}
                          disabled={isSubmitting}
                          className="pr-10"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword((previous) => !previous)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
                          aria-label={t(
                            showNewPassword
                              ? 'customer.settings.password.hidePassword'
                              : 'customer.settings.password.showPassword',
                            showNewPassword ? 'Hide password' : 'Show password'
                          )}
                          title={t(
                            showNewPassword
                              ? 'customer.settings.password.hidePassword'
                              : 'customer.settings.password.showPassword',
                            showNewPassword ? 'Hide password' : 'Show password'
                          )}
                          disabled={isSubmitting}
                        >
                          {showNewPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormDescription>
                      {t(
                        'customer.settings.password.helper',
                        "Your new password must meet your provider's security policy."
                      )}
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('customer.settings.password.confirmLabel', 'Confirm new password')}</FormLabel>
                    <FormControl>
                      <div className="relative">
                        <Input
                          type={showConfirmPassword ? 'text' : 'password'}
                          autoComplete="new-password"
                          placeholder={t(
                            'customer.settings.password.confirmPlaceholder',
                            'Re-enter your new password'
                          )}
                          disabled={isSubmitting}
                          className="pr-10"
                          {...field}
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword((previous) => !previous)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground transition hover:text-foreground"
                          aria-label={t(
                            showConfirmPassword
                              ? 'customer.settings.password.hidePassword'
                              : 'customer.settings.password.showPassword',
                            showConfirmPassword ? 'Hide password' : 'Show password'
                          )}
                          title={t(
                            showConfirmPassword
                              ? 'customer.settings.password.hidePassword'
                              : 'customer.settings.password.showPassword',
                            showConfirmPassword ? 'Hide password' : 'Show password'
                          )}
                          disabled={isSubmitting}
                        >
                          {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                        </button>
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              {passwordRules.length > 0 ? (
                <div className="rounded-md border border-muted bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
                  <p className="mb-1 text-foreground">
                    {t('auth.passwordPolicy.title', 'Password requirements')}
                  </p>
                  <ul className="list-disc space-y-1 pl-4">
                    {passwordRules.map((rule) => (
                      <li key={rule}>{rule}</li>
                    ))}
                  </ul>
                </div>
              ) : null}
              <Button type="submit" className="w-full sm:w-auto" disabled={isSubmitting}>
                {isSubmitting
                  ? t(
                      isSetFlow ? 'customer.settings.password.setSubmitting' : 'customer.settings.password.submitting',
                      isSetFlow ? 'Setting...' : 'Updating...'
                    )
                  : t(
                      isSetFlow ? 'customer.settings.password.setSubmit' : 'customer.settings.password.submit',
                      isSetFlow ? 'Set password' : 'Update password'
                    )}
              </Button>
            </form>
          </Form>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
